import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { DollarSign, Loader2, CheckCircle, Trash2, TrendingDown, PieChart, Calendar, Download } from 'lucide-react';
import YearSelector from './YearSelector';
import { useYear } from '../contexts/YearContext';
import { invalidateAccountBalances, beginSync, endSync } from '../hooks/useAccountBalances';

function ExpenseTracker() {
  const [activeTab, setActiveTab] = useState('log'); // log, view, analyze
  const [amount, setAmount] = useState('');
  const [account, setAccount] = useState('operations');
  const [description, setDescription] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [date, setDate] = useState('');
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [filterAccount, setFilterAccount] = useState('all');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, expenseId: null });
  const [customAccounts, setCustomAccounts] = useState([]);
  const [userRole, setUserRole] = useState('');
  const { selectedYear } = useYear();

  // Memoize expense table total so the O(n) reduce + toFixed only runs when
  // `expenses` actually changes, not on every keystroke elsewhere.
  const expensesTotal = useMemo(
    () => expenses.reduce((sum, e) => sum + (e.amount || 0), 0).toFixed(2),
    [expenses]
  );

  useEffect(() => {
    loadCustomAccounts();
    loadUserInfo();
  }, []);

  const loadUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/user-info`);
      setUserRole(response.data.role || '');
    } catch (err) {
      console.error('Error loading user info:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'view') {
      loadExpenses();
    } else if (activeTab === 'analyze') {
      loadSummary();
    }
  }, [activeTab, filterAccount, selectedYear]);

  const loadCustomAccounts = async () => {
    try {
      const response = await axios.get(`${API}/financial-plan`);
      if (response.data?.allocation?.custom_allocations) {
        setCustomAccounts(response.data.allocation.custom_allocations);
      }
    } catch (err) {
      console.error('Error loading custom accounts:', err);
    }
  };

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ year: selectedYear });
      if (filterAccount !== 'all') {
        params.append('account', filterAccount);
      }
      const response = await axios.get(`${API}/expenses?${params.toString()}`);
      setExpenses(response.data);
    } catch (err) {
      console.error('Error loading expenses:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/expenses/summary/monthly?year=${selectedYear}`);
      setSummary(response.data);
    } catch (err) {
      console.error('Error loading summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const logExpense = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    // Determine the description based on selected reason
    let finalDescription = '';
    if (selectedReason === 'custom') {
      if (!customReason.trim()) {
        alert('Please enter a custom reason');
        return;
      }
      finalDescription = customReason.trim();
    } else if (selectedReason) {
      finalDescription = selectedReason;
    } else {
      alert('Please select what this expense was for');
      return;
    }

    try {
      setSubmitting(true);
      beginSync('Logging expense');
      const response = await axios.post(`${API}/expenses`, {
        amount: parseFloat(amount),
        account: account,
        description: finalDescription,
        date: date || null
      });

      // An expense shifts account balances — refresh every view.
      invalidateAccountBalances();
      endSync({ success: true });

      setSuccess(true);
      setAmount('');
      setSelectedReason('');
      setCustomReason('');
      setDate('');

      // Auto-switch to Step 2 after logging
      setTimeout(() => {
        setSuccess(false);
        setActiveTab('view');
      }, 1500);
    } catch (err) {
      endSync({ success: false });
      console.error('Error logging expense:', err);
      if (err.response?.status === 403 || err.response?.status === 401) {
        alert('Session expired. Please log in again.');
        window.location.href = '/auth';
      } else {
        alert(`Failed to log expense: ${err.response?.data?.detail || err.message}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (expenseId) => {
    setConfirmDialog({ isOpen: true, expenseId });
  };

  const handleConfirmDelete = async () => {
    beginSync('Deleting expense');
    try {
      await axios.delete(`${API}/expenses/${confirmDialog.expenseId}`);
      setConfirmDialog({ isOpen: false, expenseId: null });
      loadExpenses();
      invalidateAccountBalances();
      endSync({ success: true });
    } catch (err) {
      endSync({ success: false });
      console.error('Error deleting expense:', err);
      alert('Failed to delete expense');
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialog({ isOpen: false, expenseId: null });
  };

  const baseAccountOptions = [
    { value: 'operations', label: 'Personal Expense', color: 'blue' },
    { value: 'business_growth', label: 'Business Expense', color: 'purple' }
  ];

  // Add custom allocations to account options
  const customAccountOptions = customAccounts.map(alloc => ({
    value: alloc.name,
    label: alloc.name,
    color: 'indigo'
  }));

  const accountOptions = [...baseAccountOptions, ...customAccountOptions];

  return (
    <div className="space-y-6" data-testid="expense-tracker">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Expense Tracker</h1>
            <p className="text-gray-600 dark:text-gray-300 dark:text-gray-300">3-Step System: Log → View → Analyze | Viewing: {selectedYear}</p>
          </div>
          <YearSelector />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div className="border-b border-gray-200 dark:border-gray-700 dark:border-gray-700">
          <div className="flex space-x-1 p-4">
            <button
              onClick={() => setActiveTab('log')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'log'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100'
              }`}
            >
              <DollarSign className="w-5 h-5" />
              <span>Step 1: Log Expense</span>
            </button>
            <button
              onClick={() => setActiveTab('view')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'view'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100'
              }`}
            >
              <Calendar className="w-5 h-5" />
              <span>Step 2: View Expenses</span>
            </button>
            <button
              onClick={() => setActiveTab('analyze')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'analyze'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100'
              }`}
            >
              <PieChart className="w-5 h-5" />
              <span>Analyze</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Log Expense */}
          {activeTab === 'log' && (
            <div className="space-y-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  <strong>Quick Expense Entry:</strong> Log your expense by selecting which account it came from, entering the amount, and adding a brief description.
                </p>
              </div>

              {success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-800 font-medium">Expense logged successfully!</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Amount */}
                <div>
                  <label className="block text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">
                    Amount Spent
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-12 pr-4 py-3 text-xl font-bold border-2 border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-red-50 dark:bg-red-900/20 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                    />
                  </div>
                </div>

                {/* Account */}
                <div>
                  <label className="block text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">
                    From Which Account?
                  </label>
                  <select
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white"
                  >
                    {accountOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Reason Dropdown */}
              <div>
                <label className="block text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Expense Category
                </label>
                <select
                  value={selectedReason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white"
                >
                  <option value="">-- Select Category --</option>
                  
                  {/* Agent categories - shown to everyone */}
                  <option value="TextDrip">TextDrip</option>
                  <option value="OnlySales">OnlySales</option>
                  <option value="Ringy">Ringy</option>
                  <option value="VSoft">VSoft</option>
                  <option value="Crankwheel">Crankwheel</option>
                  <option value="SG Leads">SG Leads</option>
                  <option value="Office Supplies">Office Supplies</option>
                  <option value="States">States</option>
                  <option value="Tampa USHA">Tampa USHA</option>
                  <option value="NextGen">NextGen</option>
                  <option value="Assistant">Assistant</option>
                  <option value="Charity">Charity</option>
                  
                  {/* Leader-specific categories (only for SAT, FSL, FTA) */}
                  {(userRole === 'satellite' || userRole === 'fsl' || userRole === 'fta') && (
                    <>
                      <option value="Social Media">Social Media</option>
                      <option value="Team Food">Team Food</option>
                      <option value="Agent States">Agent States</option>
                      <option value="Licensing">Licensing</option>
                      <option value="Agent Office Pack">Agent Office Pack</option>
                      <option value="Agent Milestones">Agent Milestones</option>
                      <option value="Cash Spiffs">Cash Spiffs</option>
                      <option value="Team Dinner">Team Dinner</option>
                      <option value="Team Competition">Team Competition</option>
                      <option value="Monday CRM">Monday CRM</option>
                      <option value="Indeed">Indeed</option>
                    </>
                  )}
                  
                  <option value="custom">Custom (fill in)</option>
                </select>
              </div>

              {/* Custom Reason Input (shown only when "Custom" is selected) */}
              {selectedReason === 'custom' && (
                <div>
                  <label className="block text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">
                    Enter Custom Reason
                  </label>
                  <input
                    type="text"
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="e.g., Groceries, Gas, Other expense..."
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  />
                </div>
              )}

              {/* Date (Optional) */}
              <div>
                <label className="block text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Date (Optional - defaults to today)
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-center">
                <button
                  onClick={logExpense}
                  disabled={submitting}
                  className="flex items-center space-x-3 px-8 py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-lg font-semibold shadow-lg"
                >
                  {submitting ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <TrendingDown className="w-6 h-6" />
                  )}
                  <span>{submitting ? 'Logging...' : 'Log Expense'}</span>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: View Expenses */}
          {activeTab === 'view' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white dark:text-white">All Expenses</h2>
                <div className="flex items-center space-x-3">
                  {expenses.length > 0 && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const res = await axios.get(
                            `${API}/exports/expenses.xlsx`,
                            {
                              params: {
                                year: selectedYear,
                                ...(filterAccount !== 'all' ? { account: filterAccount } : {}),
                              },
                              responseType: 'blob',
                            }
                          );
                          const url = URL.createObjectURL(res.data);
                          const slug =
                            filterAccount !== 'all'
                              ? `-${filterAccount.replace('_growth', '')}`
                              : '';
                          const filename = `papertrail-expenses-${selectedYear}${slug}.xlsx`;
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = filename;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          URL.revokeObjectURL(url);
                        } catch (err) {
                          console.error('Export failed:', err);
                          alert('Unable to export right now. Please try again.');
                        }
                      }}
                      data-testid="export-expenses-xlsx-btn"
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      <span>Export Excel (.xlsx)</span>
                    </button>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">Filter by Account:</label>
                    <select
                      value={filterAccount}
                      onChange={(e) => setFilterAccount(e.target.value)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Accounts</option>
                      {accountOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : expenses.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                  <p className="text-lg">No expenses logged yet. Start tracking your spending!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Account</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Amount</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
                      {expenses.map((expense, index) => (
                        <tr key={expense.id} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white dark:text-white">
                            {new Date(expense.date).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white dark:text-white">{expense.description}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              expense.account === 'operations' ? 'bg-blue-100 text-blue-800' :
                              expense.account === 'business_growth' ? 'bg-purple-100 text-purple-800' :
                              expense.account === 'taxes' ? 'bg-red-100 text-red-800' :
                              expense.account === 'buffer' ? 'bg-green-100 text-green-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {accountOptions.find(a => a.value === expense.account)?.label || expense.account}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-red-600 text-right">
                            ${expense.amount.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleDeleteClick(expense.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete expense"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-100 dark:bg-gray-700">
                      <tr>
                        <td colSpan="3" className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white dark:text-white">TOTAL</td>
                        <td className="px-4 py-3 text-sm font-bold text-red-600 dark:text-red-400 text-right">
                          ${expensesTotal}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Analyze */}
          {activeTab === 'analyze' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white dark:text-white">Budget vs Actual Analysis</h2>

              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : summary ? (
                <div className="space-y-6">
                  {/* Total Expenses Card */}
                  <div className="bg-gradient-to-r from-red-600 to-red-800 text-white rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-semibold mb-2">Total Expenses (Year-to-Date)</h3>
                    <p className="text-4xl font-bold">${summary.total_expenses.toLocaleString()}</p>
                  </div>

                  {/* Account Breakdown */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Operations Account */}
                    <div className="bg-white dark:bg-gray-700 dark:text-white border-2 border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Personal Expenses</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-300 dark:text-gray-300">Allocated (Budget):</span>
                          <span className="text-lg font-bold text-blue-600">
                            ${summary.allocated_amounts.operations.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-300 dark:text-gray-300">Actual Spent:</span>
                          <span className="text-lg font-bold text-red-600">
                            ${summary.actual_expenses.operations.toLocaleString()}
                          </span>
                        </div>
                        <div className="pt-2 border-t">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Remaining:</span>
                            <span className={`text-lg font-bold ${
                              summary.allocated_amounts.operations - summary.actual_expenses.operations >= 0
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}>
                              ${(summary.allocated_amounts.operations - summary.actual_expenses.operations).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Business Expenses Account */}
                    <div className="bg-white dark:bg-gray-700 dark:text-white border-2 border-purple-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Business Expenses</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-300 dark:text-gray-300">Allocated (Budget):</span>
                          <span className="text-lg font-bold text-purple-600">
                            ${summary.allocated_amounts.business_growth.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-300 dark:text-gray-300">Actual Spent:</span>
                          <span className="text-lg font-bold text-red-600">
                            ${summary.actual_expenses.business_growth.toLocaleString()}
                          </span>
                        </div>
                        <div className="pt-2 border-t">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Remaining:</span>
                            <span className={`text-lg font-bold ${
                              summary.allocated_amounts.business_growth - summary.actual_expenses.business_growth >= 0
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}>
                              ${(summary.allocated_amounts.business_growth - summary.actual_expenses.business_growth).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Other Accounts Summary */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-3">Other Account Spending</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Taxes</p>
                        <p className="text-lg font-bold text-red-700 dark:text-red-400">${summary.actual_expenses.taxes.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Buffer Pod</p>
                        <p className="text-lg font-bold text-red-700 dark:text-red-400">${summary.actual_expenses.buffer.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Income Hub</p>
                        <p className="text-lg font-bold text-red-700 dark:text-red-400">${summary.actual_expenses.income_hub.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Insights */}
                  <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      <strong>💡 Insights:</strong> Compare your allocated budget (based on your Financial Plan) with actual spending. 
                      {summary.actual_expenses.operations > summary.allocated_amounts.operations && (
                        <span className="text-red-800 dark:text-red-400 font-bold"> ⚠️ You're overspending on personal expenses!</span>
                      )}
                      {summary.actual_expenses.business_growth > summary.allocated_amounts.business_growth && (
                        <span className="text-red-800 dark:text-red-400 font-bold"> ⚠️ You're overspending on business expenses!</span>
                      )}
                      {summary.actual_expenses.operations <= summary.allocated_amounts.operations && 
                       summary.actual_expenses.business_growth <= summary.allocated_amounts.business_growth && (
                        <span className="text-green-800 dark:text-green-400 font-bold"> ✅ Great job! You're staying within your budget!</span>
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                  <p className="text-lg">No data available yet.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Custom Confirmation Dialog */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Delete Expense</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to delete this expense? This action cannot be undone.
            </p>
            <div className="flex space-x-3 justify-end">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExpenseTracker;
